/**
 * Shared tooltips (items and skills) for every panel.
 *
 * Usage from any component:
 *   <div {...tip(() => <ItemTooltip item={gear} where="bag" />)} />
 *   <img {...tip(() => <SkillTooltip skillId="rav_cleaver" />)} />
 *
 * Tooltips render into their own layer on <body> (so window transforms and
 * overflow never clip them), anchor beside the hovered element, flip/clamp to
 * the viewport, re-render on `bump()`, and hide on panel change, scroll or when
 * the anchor disappears.
 */
import { effect, signal } from '@preact/signals';
import { type ComponentChildren, render } from 'preact';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { SIGILS, STONE_NODES } from '../../game/build';
import { SKILL_LEVEL_COST, SKILL_MAX_LEVEL, type BuiltCharacter, type CharacterSave } from '../../game/character';
import { equip, itemValue } from '../../game/inventory';
import {
  COMBAT_STATS,
  COMBAT_STAT_NAMES,
  type CombatStat,
  type EquipItem,
  type GearItem,
  type ItemDef,
  ITEMS,
  type Rarity,
  SET_BONUSES,
  SET_NAMES,
  type StoneItem,
  gearIlvl,
  stonePoints,
} from '../../game/items';
import type { Progression } from '../../game/Progression';
import { registry } from '../../sim/registry';
import { skillIcon } from '../../ui/icons';
import { api } from '../../ui/app/api';
import { ui } from '../../ui/app/store';
import { itemIcon } from './art';
import {
  CAST_TYPE_COLOR,
  CAST_TYPE_LABEL,
  POSITIONAL_LABEL,
  RARITY_NAME,
  STAGGER_LABEL,
  SUPER_ARMOR_LABEL,
  TIER_COLOR,
  TIER_NUMERAL,
  UNLEVELLED_LEVEL,
  isLevelled,
  skillLevelOf,
  equipIlvl,
  equipTypeLabel,
  fmt,
  fmtPct,
  fmtSec,
  gearTier,
  qualityColor,
  skillCalc,
  stackTypeLabel,
} from './logic';

export { itemIcon } from './art';

// ====================================================================== layer

interface TipState {
  el: Element;
  content: () => ComponentChildren;
}

const tipState = signal<TipState | null>(null);
let host: HTMLDivElement | null = null;

function ensureLayer(): void {
  if (host || typeof document === 'undefined') return;
  host = document.createElement('div');
  host.className = 'btt-layer';
  document.body.appendChild(host);
  render(<TipLayer />, host);
  effect(() => {
    void ui.panel.value;
    void ui.screen.value;
    tipState.value = null;
  });
  window.addEventListener('scroll', () => hideTip(), true);
  window.addEventListener('blur', () => hideTip());
  window.addEventListener('pointerdown', (e) => {
    // Dragging or clicking elsewhere closes the tooltip; clicks on the anchor keep it.
    const t = tipState.value;
    if (t && !(e.target instanceof Node && t.el.contains(e.target))) hideTip();
  });
}

/** Shows a tooltip anchored to `el`. `content` is re-evaluated whenever game data changes. */
export function showTip(el: Element, content: () => ComponentChildren): void {
  ensureLayer();
  tipState.value = { el, content };
}

/** Hides the tooltip (only if it belongs to `el`, when given). */
export function hideTip(el?: Element | null): void {
  if (!tipState.value) return;
  if (!el || tipState.value.el === el) tipState.value = null;
}

/** Event props for a hover/focus target. Spread onto any element. */
export function tip(content: () => ComponentChildren): {
  onMouseEnter: (e: MouseEvent) => void;
  onMouseLeave: (e: MouseEvent) => void;
  onFocus: (e: FocusEvent) => void;
  onBlur: (e: FocusEvent) => void;
} {
  return {
    onMouseEnter: (e) => showTip(e.currentTarget as Element, content),
    onMouseLeave: (e) => hideTip(e.currentTarget as Element),
    onFocus: (e) => showTip(e.currentTarget as Element, content),
    onBlur: (e) => hideTip(e.currentTarget as Element),
  };
}

function TipLayer() {
  const t = tipState.value;
  void ui.version.value;
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const box = ref.current;
    if (!t || !box) return;
    if (!t.el.isConnected) {
      tipState.value = null;
      return;
    }
    const r = t.el.getBoundingClientRect();
    const w = box.offsetWidth;
    const h = box.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 10;
    // Beside the anchor (right, else left); if neither fits, below (else above) it.
    let x = r.right + gap;
    let y = r.top;
    if (x + w > vw - 8) {
      x = r.left - gap - w;
      if (x < 8) {
        x = Math.max(8, Math.min(vw - w - 8, r.left));
        y = r.bottom + gap + h <= vh - 8 ? r.bottom + gap : r.top - gap - h;
      }
    }
    y = Math.max(8, Math.min(vh - 8 - h, y));
    box.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
    box.style.visibility = 'visible';
  });
  if (!t) return null;
  const body = attempt(t.content, null);
  if (!body) return null;
  return (
    <div ref={ref} class="btt" role="tooltip" style={{ visibility: 'hidden' }}>
      {body}
    </div>
  );
}

// ====================================================================== shared bits

/** Quality bar (0–100) coloured by tier. */
export function QualityBar({ q, label = true, thin = false }: { q: number; label?: boolean; thin?: boolean }) {
  const c = qualityColor(q);
  return (
    <div class={`bq${thin ? ' bq--thin' : ''}`}>
      {label && <span class="bq-l">Quality</span>}
      <div class="bq-track">
        <i style={{ width: `${Math.max(3, Math.min(100, q))}%`, background: `linear-gradient(90deg, color-mix(in srgb, ${c} 35%, #000), ${c})` }} />
      </div>
      {label && (
        <b class="bq-v" style={{ color: c }}>
          {q}
        </b>
      )}
    </div>
  );
}

function Divider() {
  return (
    <div class="btt-div">
      <i />
    </div>
  );
}

const classWeapon = (c: CharacterSave | undefined): string | undefined => (c ? registry.classes.get(c.classId)?.weapon : undefined);

/** Runs `fn`, returning `fallback` if it throws (tooltips must never break the page). */
function attempt<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

function prog(): Progression | null {
  return attempt(() => api().progression, null);
}

/** Builds the character as if `mutate` had been applied (uses Progression.build on a clone). */
export function buildWith(p: Progression, mutate: (c: CharacterSave) => void): BuiltCharacter {
  const clone = structuredClone(p.char);
  mutate(clone);
  const orig = p.char;
  p.char = clone;
  try {
    return p.build();
  } finally {
    p.char = orig;
  }
}

/** Equipment slot key currently holding `uid`, if any. */
export function slotOf(c: CharacterSave, uid: string): string | null {
  const eq = c.equipment as Record<string, EquipItem | undefined>;
  for (const [k, v] of Object.entries(eq)) if (v?.uid === uid) return k;
  return null;
}

/** Stat contribution of an item and, for bag items, the change if it were equipped. */
function equipImpact(p: Progression, item: EquipItem): { own: { atk: number; hp: number } | null; cmp: { ilvl: number; atk: number; hp: number; combat: Partial<Record<CombatStat, number>> } | null } {
  const now = p.build();
  const slot = slotOf(p.char, item.uid);
  if (slot) {
    const without = buildWith(p, (c) => {
      delete (c.equipment as Record<string, unknown>)[slot];
    });
    return { own: { atk: now.summary.attack - without.summary.attack, hp: now.summary.maxHp - without.summary.maxHp }, cmp: null };
  }
  if (!p.char.inventory.items.some((i) => i.uid === item.uid)) return { own: null, cmp: null };
  const hold = { slot: null as string | null };
  const withIt = buildWith(p, (c) => {
    equip(c, item.uid);
    hold.slot = slotOf(c, item.uid);
  });
  const target = hold.slot;
  const bare = target
    ? buildWith(p, (c) => {
        equip(c, item.uid);
        delete (c.equipment as Record<string, unknown>)[target];
      })
    : now;
  const combat: Partial<Record<CombatStat, number>> = {};
  for (const s of COMBAT_STATS) combat[s] = (withIt.combat[s] ?? 0) - (now.combat[s] ?? 0);
  return {
    own: { atk: withIt.summary.attack - bare.summary.attack, hp: withIt.summary.maxHp - bare.summary.maxHp },
    cmp: { ilvl: withIt.ilvl - now.ilvl, atk: withIt.summary.attack - now.summary.attack, hp: withIt.summary.maxHp - now.summary.maxHp, combat },
  };
}

function Delta({ label, v, digits = 0, pct = false }: { label: string; v: number; digits?: number; pct?: boolean }) {
  if (Math.abs(v) < (pct ? 0.0005 : digits ? 0.005 : 0.5)) return null;
  const up = v > 0;
  const txt = pct ? fmtPct(Math.abs(v)) : digits ? Math.abs(v).toFixed(digits) : fmt(Math.abs(v));
  return (
    <div class="btt-line">
      <span>{label}</span>
      <b class={up ? 'btt-up' : 'btt-down'}>
        {up ? '▲' : '▼'} {up ? '+' : '−'}
        {txt}
      </b>
    </div>
  );
}

// ====================================================================== item tooltip

export interface ItemTooltipProps {
  /** Equipment instance, catalog def, or catalog id. */
  item: EquipItem | ItemDef | string;
  /** Stack count shown for stackables (defaults to the bag count). */
  count?: number;
  /** Where the item is: `bag` adds a comparison with the equipped item. */
  where?: 'bag' | 'equipped' | 'shop' | 'other';
  /** Hint lines at the bottom (e.g. "Right-click to equip"). */
  hints?: string[];
  /** Price line override (shops). */
  price?: { label: string; silver?: number; gold?: number };
}

export function ItemTooltip(props: ItemTooltipProps) {
  const x = props.item;
  if (typeof x === 'string') {
    const def = ITEMS[x];
    return def ? <StackTip {...props} def={def} /> : null;
  }
  if ('uid' in x) return <EquipTip {...props} item={x} />;
  return <StackTip {...props} def={x} />;
}

function Head({ name, rarity, plus }: { name: string; rarity: Rarity; plus?: number }) {
  return (
    <div class={`btt-head btt-head--${rarity}`}>
      <span class={`btt-name r-${rarity}`}>
        {plus ? <span class="btt-plus">+{plus} </span> : null}
        {name}
      </span>
    </div>
  );
}

/** Silver / gold amounts with coin glyphs (each shown only when given). */
export function Money({ silver, gold, class: cls = '' }: { silver?: number; gold?: number; class?: string }) {
  return (
    <span class={`bmoney ${cls}`}>
      {silver !== undefined && (
        <span class="bmoney-s">
          <i class="bcoin bcoin--s" />
          {fmt(silver)}
        </span>
      )}
      {gold !== undefined && (
        <span class="bmoney-g">
          <i class="bcoin bcoin--g" />
          {fmt(gold)}
        </span>
      )}
    </span>
  );
}

function Footer({ bound, sell, price, hints }: { bound?: boolean; sell?: number; price?: ItemTooltipProps['price']; hints?: string[] }) {
  return (
    <div class="btt-foot">
      {bound !== undefined && <div class={bound ? 'btt-bound' : 'btt-dim'}>{bound ? 'Bound to character' : 'Tradable'}</div>}
      {price ? (
        <div class="btt-line">
          <span class="btt-dim">{price.label}</span>
          <Money silver={price.silver} gold={price.gold} />
        </div>
      ) : sell !== undefined && sell > 0 ? (
        <div class="btt-line">
          <span class="btt-dim">Sell price</span>
          <Money silver={sell} />
        </div>
      ) : null}
      {hints?.map((h) => (
        <div key={h} class="btt-hint">
          {h}
        </div>
      ))}
    </div>
  );
}

function EquipTip({ item, where, hints, price }: Omit<ItemTooltipProps, 'item'> & { item: EquipItem }) {
  void ui.version.value;
  const p = prog();
  const weapon = classWeapon(p?.char);
  const ilvl = equipIlvl(item);
  const plus = item.kind === 'gear' ? item.plus : 0;
  const isEquipped = where === 'equipped' || (!!p && !!slotOf(p.char, item.uid));

  // Contribution of this item (with vs without it in its slot) and, for bag items, the change if equipped.
  let impact: ReturnType<typeof equipImpact> = { own: null, cmp: null };
  if (p && item.kind !== 'stone' && where !== 'shop') {
    try {
      impact = equipImpact(p, item);
    } catch {
      impact = { own: null, cmp: null };
    }
  }
  const { own, cmp } = impact;

  return (
    <div class="btt-item">
      <Head name={item.name} rarity={item.rarity} plus={plus} />
      <div class="btt-id">
        <div class={`btt-icon rbg-${item.rarity}`}>
          <img src={itemIcon(item, weapon)} alt="" />
        </div>
        <div class="btt-idtext">
          <div class={`r-${item.rarity}`}>
            {RARITY_NAME[item.rarity]} {equipTypeLabel(item)}
          </div>
          {ilvl !== null && (
            <div class="btt-ilvl">
              Item Level <b>{fmt(ilvl)}</b> <span class="btt-dim">(Tier {gearTier(item.kind === 'gear' ? item.baseIlvl : ilvl)})</span>
            </div>
          )}
          {isEquipped && <div class="btt-equipped">Equipped</div>}
        </div>
      </div>
      {item.kind !== 'stone' && <QualityBar q={item.quality} />}
      <Divider />
      {item.kind === 'gear' && <GearBody g={item} own={own} />}
      {item.kind === 'accessory' && (
        <>
          <div class="btt-sec">
            <div class="btt-h">Base Effects</div>
            {own && own.hp > 0 ? (
              <div class="btt-line">
                <span>Max HP</span>
                <b>+{fmt(own.hp)}</b>
              </div>
            ) : (
              <div class="btt-line">
                <span>Max HP</span>
                <b>+{(1.5 + item.quality / 100).toFixed(1)}%</b>
              </div>
            )}
          </div>
          <div class="btt-sec">
            <div class="btt-h">Additional Effects</div>
            {(Object.entries(item.stats) as [CombatStat, number][]).map(([k, v]) => (
              <div key={k} class="btt-line">
                <span>{COMBAT_STAT_NAMES[k]}</span>
                <b class="btt-green">+{fmt(v)}</b>
              </div>
            ))}
          </div>
          {item.sigils.length > 0 && (
            <div class="btt-sec">
              <div class="btt-h">Sigils</div>
              {item.sigils.map((s) => (
                <div key={s.id} class="btt-line">
                  <span class={SIGILS[s.id]?.negative ? 'btt-neg' : 'btt-sigil'}>[{SIGILS[s.id]?.name ?? s.id}]</span>
                  <b class={SIGILS[s.id]?.negative ? 'btt-neg' : ''}>+{s.pts} pts</b>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {item.kind === 'stone' && <StoneBody s={item} />}
      {cmp && (cmp.atk || cmp.hp || cmp.ilvl || Object.values(cmp.combat).some((v) => v)) ? (
        <div class="btt-sec btt-cmp">
          <div class="btt-h">If equipped</div>
          <Delta label="Item Level" v={cmp.ilvl} digits={2} />
          <Delta label="Attack Power" v={cmp.atk} />
          <Delta label="Max HP" v={cmp.hp} />
          {COMBAT_STATS.map((s) => (
            <Delta key={s} label={COMBAT_STAT_NAMES[s]} v={cmp.combat[s] ?? 0} />
          ))}
        </div>
      ) : null}
      <Footer bound={item.bound} sell={where === 'shop' ? undefined : itemValue(item)} price={price} hints={hints} />
    </div>
  );
}

function GearBody({ g, own }: { g: GearItem; own: { atk: number; hp: number } | null }) {
  const p = prog();
  const weapon = g.slot === 'weapon';
  const pieces = g.set && p ? Object.values(p.char.equipment).filter((e) => e && e.kind === 'gear' && e.set === g.set).length : 0;
  return (
    <>
      <div class="btt-sec">
        <div class="btt-h">Base Effects</div>
        {own ? (
          <>
            {own.atk > 0 && (
              <div class="btt-line">
                <span>Attack Power</span>
                <b>+{fmt(own.atk)}</b>
              </div>
            )}
            {own.hp > 0 && (
              <div class="btt-line">
                <span>Max HP</span>
                <b>+{fmt(own.hp)}</b>
              </div>
            )}
          </>
        ) : (
          <div class="btt-line">
            <span>{weapon ? 'Weapon Power' : 'Armour Power'}</span>
            <b>{fmt(gearIlvl(g))}</b>
          </div>
        )}
      </div>
      <div class="btt-sec">
        <div class="btt-h">Additional Effects</div>
        <div class="btt-line">
          <span>{weapon ? 'Damage' : 'Max HP'}</span>
          <b class="btt-green">+{weapon ? fmtPct(0.1 * (g.quality / 100)) : fmtPct(0.012 * (g.quality / 100), 2)}</b>
        </div>
      </div>
      <div class="btt-sec">
        <div class="btt-line">
          <span>Tempering</span>
          <b class="btt-gold">
            +{g.plus} / +{g.maxPlus}
          </b>
        </div>
        {g.resolve > 0 && g.plus < g.maxPlus && (
          <div class="btt-line">
            <span>Forge Resolve</span>
            <b class="btt-resolve">{fmtPct(g.resolve)}</b>
          </div>
        )}
      </div>
      {g.set && (
        <div class="btt-sec">
          <div class="btt-h btt-set">
            {SET_NAMES[g.set] ?? g.set} set <span class="btt-dim">({pieces}/6 equipped)</span>
          </div>
          {(SET_BONUSES[g.set] ?? []).map((b) => (
            <div key={b.pieces} class={`btt-line ${pieces >= b.pieces ? 'btt-green' : 'btt-dim'}`}>
              <span>{b.pieces}-piece</span>
              <span>{b.text}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** Rune stone lines with node pips. */
export function StonePips({ s, line }: { s: StoneItem; line: number }) {
  const l = s.lines[line];
  if (!l) return null;
  return (
    <span class={`bpips ${l.negative ? 'bpips--neg' : ''}`}>
      {l.nodes.map((n, i) => (
        <i key={i} class={n === true ? 'on' : n === false ? 'off' : ''} />
      ))}
    </span>
  );
}

function StoneBody({ s }: { s: StoneItem }) {
  return (
    <div class="btt-sec">
      <div class="btt-h">Etched Sigils</div>
      {s.lines.map((l, i) => (
        <div key={i} class="btt-stone">
          <div class="btt-line">
            <span class={l.negative ? 'btt-neg' : 'btt-sigil'}>[{SIGILS[l.sigil]?.name ?? l.sigil}]</span>
            <b class={l.negative ? 'btt-neg' : ''}>+{stonePoints(l)} pts</b>
          </div>
          <StonePips s={s} line={i} />
        </div>
      ))}
      <div class="btt-line btt-dim">
        <span>Etching chance</span>
        <span>
          {Math.round(s.chance * 100)}% · {STONE_NODES[s.rarity]} nodes per line
        </span>
      </div>
    </div>
  );
}

function StackTip({ def, count, hints, price, where }: Omit<ItemTooltipProps, 'item'> & { def: ItemDef }) {
  void ui.version.value;
  const p = prog();
  const owned = count ?? (p ? (p.char.inventory.stacks[def.id] ?? 0) : 0);
  const learned = def.kind === 'book' && def.ref && p ? (p.char.sigilBooks[def.ref] ?? 0) : null;
  const sig = def.kind === 'book' && def.ref ? SIGILS[def.ref] : undefined;
  return (
    <div class="btt-item">
      <Head name={def.name} rarity={def.rarity} />
      <div class="btt-id">
        <div class={`btt-icon rbg-${def.rarity}`}>
          <img src={itemIcon(def)} alt="" />
        </div>
        <div class="btt-idtext">
          <div class={`r-${def.rarity}`}>
            {RARITY_NAME[def.rarity]} {stackTypeLabel(def)}
          </div>
          {where !== 'shop' && <div class="btt-dim">Owned: {fmt(owned)}</div>}
          {def.stack > 1 && <div class="btt-dim">Stacks to {fmt(def.stack)}</div>}
        </div>
      </div>
      <Divider />
      <div class="btt-sec">
        <div class="btt-desc">{def.description}</div>
        {def.battle && (
          <div class="btt-line btt-dim">
            <span>Cooldown {def.battle.cooldown}s</span>
            <span>{def.battle.limit} uses per instance</span>
          </div>
        )}
      </div>
      {sig && learned !== null && (
        <div class="btt-sec">
          <div class="btt-line">
            <span class="btt-sigil">[{sig.name}]</span>
            <b>{learned} / 20 learned</b>
          </div>
          {sig.levels.map((t, i) => (
            <div key={i} class={`btt-lvl ${learned >= (i + 1) * 5 ? 'btt-green' : 'btt-dim'}`}>
              Lv.{i + 1} {t}
            </div>
          ))}
        </div>
      )}
      <Footer sell={where === 'shop' ? undefined : def.value} price={price} hints={hints} />
    </div>
  );
}

// ====================================================================== skill tooltip

export interface SkillTooltipProps {
  skillId: string;
  /** Level to describe (defaults to the character's current level of the skill). */
  level?: number;
  /** Facet choice to describe (defaults to the character's picks). */
  tripods?: (string | null)[];
  hints?: string[];
}

const KIND_LABEL: Record<string, string> = { awakening: 'Apex Art', identity: 'Resonance', dash: 'Movement', basic: 'Basic Attack' };

export function SkillTooltip({ skillId, level, tripods, hints }: SkillTooltipProps) {
  void ui.version.value;
  const def = registry.skills.get(skillId);
  if (!def) return null;
  const p = prog();
  const c = p?.char;
  const cls = c ? registry.classes.get(c.classId) : undefined;
  const lvl = level ?? (c ? skillLevelOf(c, def) : isLevelled(def) ? 1 : UNLEVELLED_LEVEL);
  const picks = tripods ?? c?.tripods[skillId] ?? [];
  const built: BuiltCharacter | null = p ? attempt(() => p.build(), null) : null;
  const calc = skillCalc(def, lvl, picks, built);
  const s = calc.resolved;
  const facets = (registry.tripods.get(skillId) ?? []).filter((t) => picks.includes(t.id) && s.tripods.includes(t.id));
  const gem = built?.skillMods[skillId];
  const ct = s.castType;
  return (
    <div class="btt-skill">
      <div class="btt-head btt-head--skill">
        <span class="btt-name">{def.name}</span>
      </div>
      <div class="btt-id">
        <div class="btt-icon btt-icon--skill">
          <img src={skillIcon(def, cls?.hue)} alt="" />
        </div>
        <div class="btt-idtext">
          <div>
            <span style={{ color: CAST_TYPE_COLOR[ct] }}>{CAST_TYPE_LABEL[ct]}</span>
            <span class="btt-dim"> · {KIND_LABEL[def.kind ?? 'skill'] ?? 'Skill'}</span>
          </div>
          <div>
            Cooldown <b>{fmtSec(calc.cooldown)}</b>
            {Math.abs(calc.cooldown - def.cooldown) > 0.05 && <span class="btt-dim"> (base {fmtSec(def.cooldown)})</span>}
          </div>
          <div>
            {isLevelled(def) && (
              <>
                Skill Lv. <b class="btt-gold">{lvl}</b>
                {'  '}
              </>
            )}
            {calc.mana > 0 && <span class="btt-mana">Mana {calc.mana}</span>}
          </div>
        </div>
      </div>
      <Divider />
      <div class="btt-sec">
        <div class="btt-desc">{def.description}</div>
        {calc.coef > 0 && (
          <div class="btt-dmg">
            Deals <b>{fmt(calc.damage)}</b> damage <span class="btt-dim">({Math.round(calc.coef * 100)}% of Attack Power)</span>
          </div>
        )}
        {calc.peak && (
          <div class="btt-dmg">
            {calc.peak.label}: <b>{fmt(calc.peak.damage)}</b> <span class="btt-dim">({Math.round(calc.peak.coef * 100)}%)</span>
          </div>
        )}
      </div>
      <div class="btt-sec btt-attrs">
        {s.stagger !== 'none' && <div>Stagger: {STAGGER_LABEL[s.stagger]}</div>}
        {(s.weakPoint ?? 0) > 0 && <div>Weak Point: Lv. {s.weakPoint}</div>}
        {s.positional !== 'none' && <div>Attack Type: {POSITIONAL_LABEL[s.positional]}</div>}
        {s.superArmor !== 'none' && <div>Super Armor: {SUPER_ARMOR_LABEL[s.superArmor]}</div>}
        {s.counter && <div>Counter: Yes</div>}
        {s.element && s.element !== 'none' && <div>Element: {s.element[0]!.toUpperCase() + s.element.slice(1)}</div>}
      </div>
      {facets.length > 0 && (
        <div class="btt-sec">
          <div class="btt-h">Facets</div>
          {facets.map((t) => (
            <div key={t.id} class="btt-facet">
              <b style={{ color: TIER_COLOR[t.tier - 1] }}>
                {TIER_NUMERAL[t.tier - 1]} {t.name}
              </b>
              <span>{t.description}</span>
            </div>
          ))}
        </div>
      )}
      {gem && (gem.dmg || gem.cd) ? (
        <div class="btt-sec btt-green">
          Gems: {[gem.dmg ? `damage +${fmtPct(gem.dmg, 0)}` : '', gem.cd ? `cooldown −${fmtPct(gem.cd, 0)}` : ''].filter(Boolean).join(', ')}
        </div>
      ) : null}
      {isLevelled(def) && lvl < SKILL_MAX_LEVEL && (
        <div class="btt-foot">
          <div class="btt-dim">
            Next level costs {SKILL_LEVEL_COST[lvl + 1] ?? '—'} skill point{(SKILL_LEVEL_COST[lvl + 1] ?? 0) === 1 ? '' : 's'}
          </div>
        </div>
      )}
      {hints && (
        <div class="btt-foot">
          {hints.map((h) => (
            <div key={h} class="btt-hint">
              {h}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Convenience: tooltip props for a skill icon. */
export const skillTip = (skillId: string, extra?: Omit<SkillTooltipProps, 'skillId'>): ReturnType<typeof tip> => tip(() => <SkillTooltip skillId={skillId} {...extra} />);
/** Convenience: tooltip props for an item. */
export const itemTip = (item: ItemTooltipProps['item'], extra?: Omit<ItemTooltipProps, 'item'>): ReturnType<typeof tip> => tip(() => <ItemTooltip item={item} {...extra} />);
